<template>
  <div class="latest-games-page">
    <section class="latest-games-page__hero">
      <span class="latest-games-page__eyebrow">Latest Games</span>
      <h1>Latest Games</h1>
      <p>
        Real game candidates pulled from Steam Popular New Releases, ranked for quick standalone-site screening.
      </p>
    </section>

    <p v-if="paginatedGameHubs.length === 0" class="latest-games-page__empty">
      No game data found.
    </p>

    <div v-else class="latest-games-page__list">
      <article
        v-for="hub in paginatedGameHubs"
        :key="hub.slug"
        class="latest-games-page__item"
      >
        <a
          class="latest-games-page__thumb-link"
          :href="hub.url"
          :aria-label="hub.name"
        >
          <img
            v-if="hub.image"
            :src="hub.image"
            :alt="hub.name"
            class="latest-games-page__thumb"
            loading="lazy"
          />
          <div v-else class="latest-games-page__thumb latest-games-page__thumb--fallback">
            <span>{{ hub.sourceLabel || 'Steam' }}</span>
          </div>
        </a>

        <div class="latest-games-page__body">
          <div class="latest-games-page__meta">
            <span class="latest-games-page__badge">{{ hub.sourceLabel || 'Steam' }}</span>
            <time :datetime="hub.lastSeen || hub.updateDate">{{ formatDate(hub.lastSeen || hub.updateDate) }}</time>
            <span class="latest-games-page__stat">#{{ hub.rank }}</span>
            <span v-if="hub.articleCount" class="latest-games-page__stat">
              {{ hub.articleCount }} related articles
            </span>
          </div>
          <h2 class="latest-games-page__title">
            <a :href="hub.url">{{ hub.name }}</a>
          </h2>
          <p class="latest-games-page__excerpt">
            {{ hub.description }}
          </p>
          <div v-if="hub.tags && hub.tags.length" class="latest-games-page__tags">
            <span
              v-for="tag in hub.tags.slice(0, 4)"
              :key="`${hub.slug}-${tag}`"
              class="latest-games-page__tag"
            >
              {{ tag }}
            </span>
          </div>
          <a :href="hub.url" class="latest-games-page__cta">
            Open Game Hub
          </a>
        </div>
      </article>
    </div>

    <Pagination
      v-if="totalPages > 1"
      :current-page="currentPage"
      :total-pages="totalPages"
      base-url="/games/"
      query-mode
    />
  </div>
</template>

<script>
import Pagination from '@/components/common/Pagination.vue'
import { generateListPageMetaInfo, buildBreadcrumbs } from '@/utils/seo'

const PAGE_SIZE = 12

export default {
  name: 'LatestGamesPage',
  components: {
    Pagination,
  },
  asyncData({ store }) {
    return store.dispatch('gameHubs/fetchGameHubs')
  },
  computed: {
    allGameHubs() {
      return (this.$store.state.gameHubs.gameHubs || [])
        .slice()
        .sort((a, b) => {
          const rankA = Number(a.rank || Number.MAX_SAFE_INTEGER)
          const rankB = Number(b.rank || Number.MAX_SAFE_INTEGER)
          if (rankA !== rankB) return rankA - rankB
          return new Date(b.lastSeen || b.updateDate || 0) - new Date(a.lastSeen || a.updateDate || 0)
        })
    },
    currentPage() {
      const page = Number(this.$route.query.page || 1)
      return Number.isFinite(page) && page > 0 ? page : 1
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.allGameHubs.length / PAGE_SIZE))
    },
    paginatedGameHubs() {
      const start = (this.currentPage - 1) * PAGE_SIZE
      return this.allGameHubs.slice(start, start + PAGE_SIZE)
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    generateListPageMetaInfo,
  },
  metaInfo() {
    return generateListPageMetaInfo({
      title: 'Latest Games',
      description: 'Browse real game candidates from Steam Popular New Releases, enriched with GamesNest coverage counts for standalone-site evaluation.',
      path: this.$route.fullPath,
      breadcrumbs: buildBreadcrumbs(this.$route, {
        current: 'Latest Games',
      }),
    })
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.latest-games-page {
  max-width: rem(1080);
  margin: 0 auto;
  padding: rem(24);

  @include pc {
    padding: rem(40) rem(24) rem(64);
  }

  &__hero {
    margin-bottom: rem(28);
    padding-bottom: rem(20);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    h1 {
      margin: rem(8) 0 rem(12);
      font-size: rem(34);
      line-height: 1.1;

      @include pc {
        font-size: rem(48);
      }
    }

    p {
      max-width: rem(720);
      color: $text-secondary;
      font-size: rem(16);
      line-height: 1.7;
    }
  }

  &__eyebrow {
    display: inline-flex;
    align-items: center;
    padding: rem(6) rem(10);
    border-radius: rem(999);
    background: rgba($primary-color, 0.12);
    color: $primary-color;
    font-size: rem(12);
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: rem(18);
  }

  &__item {
    display: grid;
    grid-template-columns: 1fr;
    gap: rem(16);
    padding: rem(16);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: rem(18);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.015));

    @include pc {
      grid-template-columns: rem(260) minmax(0, 1fr);
      align-items: stretch;
      padding: rem(20);
    }
  }

  &__thumb-link {
    display: block;
  }

  &__thumb {
    width: 100%;
    height: rem(200);
    object-fit: cover;
    border-radius: rem(14);
    background: rgba(255, 255, 255, 0.04);

    @include pc {
      height: 100%;
      min-height: rem(170);
    }
  }

  &__thumb--fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-secondary;
    font-size: rem(14);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: rem(12);
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: rem(10);
    color: $text-secondary;
    font-size: rem(13);
  }

  &__badge,
  &__stat,
  &__tag {
    display: inline-flex;
    align-items: center;
    padding: rem(4) rem(8);
    border-radius: rem(999);
    background: rgba(255, 255, 255, 0.06);
    color: $text-primary;
    font-weight: 600;
  }

  &__title {
    font-size: rem(24);
    line-height: 1.2;

    a {
      color: $text-primary;
      text-decoration: none;
    }
  }

  &__excerpt {
    color: $text-secondary;
    line-height: 1.7;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: rem(8);
  }

  &__tag {
    color: $text-secondary;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: rem(148);
    height: rem(44);
    padding: 0 rem(18);
    border-radius: rem(999);
    background: $primary-color;
    color: #fff;
    font-weight: 700;
    text-decoration: none;
  }

  &__empty {
    padding: rem(48) 0;
    color: $text-secondary;
    text-align: center;
  }
}
</style>
